import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  BarChart3, Users, CreditCard, Mail, MessageSquare, RefreshCw,
  TrendingUp, UserCheck, IndianRupee, DollarSign, Download, Search,
  ChevronLeft, ChevronRight, ShieldCheck, Video, Trash2, Save, ExternalLink
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`} className="bg-white rounded-2xl border border-slate-100 p-5 flex items-start gap-4">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <p className="text-2xl font-bold text-slate-900 leading-tight">{value}</p>
        <p className="text-sm text-slate-500 mt-0.5">{label}</p>
        {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
      </div>
    </div>
  );
}

function DataTable({ columns, data, emptyText, testId }) {
  if (!data || data.length === 0) {
    return (
      <div className="p-12 text-center text-slate-400" data-testid={`${testId}-empty`}>
        {emptyText || 'No data found.'}
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <Table data-testid={testId}>
        <TableHeader>
          <TableRow className="border-b border-slate-100">
            {columns.map((col) => (
              <TableHead key={col.key} className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                {col.label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((row, i) => (
            <TableRow key={i} className="border-b border-slate-50 hover:bg-slate-50/50">
              {columns.map((col) => (
                <TableCell key={col.key} className="text-sm text-slate-700">
                  {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '—')}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    paid: 'bg-emerald-50 text-emerald-700',
    active: 'bg-emerald-50 text-emerald-700',
    registered: 'bg-blue-50 text-blue-700',
    initiated: 'bg-amber-50 text-amber-700',
    created: 'bg-amber-50 text-amber-700',
    cancelled: 'bg-red-50 text-red-700',
    cancelling: 'bg-orange-50 text-orange-700',
    expired: 'bg-slate-100 text-slate-500',
  };
  return (
    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${styles[status] || 'bg-slate-100 text-slate-600'}`}>
      {status}
    </span>
  );
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function Pagination({ total, skip, limit, onPageChange }) {
  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(skip / limit) + 1;
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100" data-testid="pagination">
      <p className="text-xs text-slate-500">Showing {skip + 1}–{Math.min(skip + limit, total)} of {total}</p>
      <div className="flex gap-1">
        <Button variant="ghost" size="sm" disabled={currentPage <= 1} onClick={() => onPageChange(skip - limit)} data-testid="pagination-prev">
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="sm" disabled={currentPage >= totalPages} onClick={() => onPageChange(skip + limit)} data-testid="pagination-next">
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  // Tab data states
  const [registrations, setRegistrations] = useState({ items: [], total: 0 });
  const [subscriptions, setSubscriptions] = useState({ items: [], total: 0 });
  const [leads, setLeads] = useState({ items: [], total: 0 });
  const [contacts, setContacts] = useState({ items: [], total: 0 });
  const [transactions, setTransactions] = useState({ items: [], total: 0 });

  // Pagination
  const [regSkip, setRegSkip] = useState(0);
  const [subSkip, setSubSkip] = useState(0);
  const [leadSkip, setLeadSkip] = useState(0);
  const [contactSkip, setContactSkip] = useState(0);
  const [txSkip, setTxSkip] = useState(0);

  // Search
  const [searchTerm, setSearchTerm] = useState('');

  // Webinar
  const [webinarSettings, setWebinarSettings] = useState(null);
  const [webinarForm, setWebinarForm] = useState({});
  const [webinarSaving, setWebinarSaving] = useState(false);

  // Pricing
  const [pricingData, setPricingData] = useState({ price_inr: 499, price_usd: 5.99 });
  const [pricingForm, setPricingForm] = useState({ price_inr: '', price_usd: '' });
  const [pricingSaving, setPricingSaving] = useState(false);

  const LIMIT = 25;

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchStats();
    fetchPricing();
  }, [user, authLoading, navigate]);

  const fetchPricing = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/pricing`, { withCredentials: true });
      setPricingData(data);
      setPricingForm({ price_inr: data.price_inr, price_usd: data.price_usd });
    } catch {}
  };

  const savePricing = async () => {
    setPricingSaving(true);
    try {
      const updates = {};
      if (pricingForm.price_inr && Number(pricingForm.price_inr) !== pricingData.price_inr) {
        updates.price_inr = Number(pricingForm.price_inr);
      }
      if (pricingForm.price_usd && Number(pricingForm.price_usd) !== pricingData.price_usd) {
        updates.price_usd = Number(pricingForm.price_usd);
      }
      if (Object.keys(updates).length > 0) {
        await axios.put(`${API}/admin/pricing`, updates, { withCredentials: true });
        await fetchPricing();
      }
    } catch {}
    setPricingSaving(false);
  };

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch admin stats', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRegistrations = useCallback(async (skip = 0) => {
    try {
      const { data } = await axios.get(`${API}/admin/registrations?skip=${skip}&limit=${LIMIT}`, { withCredentials: true });
      setRegistrations(data);
    } catch {}
  }, []);

  const fetchSubscriptions = useCallback(async (skip = 0) => {
    try {
      const { data } = await axios.get(`${API}/admin/subscriptions?skip=${skip}&limit=${LIMIT}`, { withCredentials: true });
      setSubscriptions(data);
    } catch {}
  }, []);

  const fetchLeads = useCallback(async (skip = 0) => {
    try {
      const { data } = await axios.get(`${API}/admin/leads?skip=${skip}&limit=${LIMIT}`, { withCredentials: true });
      setLeads(data);
    } catch {}
  }, []);

  const fetchContacts = useCallback(async (skip = 0) => {
    try {
      const { data } = await axios.get(`${API}/admin/contacts?skip=${skip}&limit=${LIMIT}`, { withCredentials: true });
      setContacts(data);
    } catch {}
  }, []);

  const fetchTransactions = useCallback(async (skip = 0) => {
    try {
      const { data } = await axios.get(`${API}/admin/transactions?skip=${skip}&limit=${LIMIT}`, { withCredentials: true });
      setTransactions(data);
    } catch {}
  }, []);

  const fetchWebinar = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/webinar`, { withCredentials: true });
      setWebinarSettings(data);
      setWebinarForm({
        youtube_id: data.youtube_id || '',
        title: data.title || '',
        description: data.description || '',
        scheduled_at: data.scheduled_at || '',
        allow_on_demand: data.allow_on_demand ?? true,
        chat_enabled: data.chat_enabled ?? true,
        is_active: data.is_active ?? true,
        require_payment: data.require_payment ?? true,
      });
    } catch {}
  }, []);

  const saveWebinar = async () => {
    setWebinarSaving(true);
    try {
      const updates = {};
      Object.keys(webinarForm).forEach(k => {
        if (webinarForm[k] !== (webinarSettings?.[k] ?? '')) {
          updates[k] = webinarForm[k] || null;
        }
      });
      // Always send boolean fields
      updates.allow_on_demand = webinarForm.allow_on_demand;
      updates.chat_enabled = webinarForm.chat_enabled;
      updates.is_active = webinarForm.is_active;
      updates.require_payment = webinarForm.require_payment;
      if (webinarForm.youtube_id) updates.youtube_id = webinarForm.youtube_id;
      if (webinarForm.title) updates.title = webinarForm.title;
      if (webinarForm.scheduled_at) updates.scheduled_at = webinarForm.scheduled_at;
      else updates.scheduled_at = null;

      await axios.put(`${API}/admin/webinar`, updates, { withCredentials: true });
      await fetchWebinar();
    } catch {}
    setWebinarSaving(false);
  };

  const clearChat = async () => {
    if (!window.confirm('Are you sure you want to clear all webinar chat messages?')) return;
    try {
      await axios.delete(`${API}/admin/webinar/chat`, { withCredentials: true });
    } catch {}
  };

  // Fetch tab data on tab change
  useEffect(() => {
    if (tab === 'registrations') fetchRegistrations(regSkip);
    else if (tab === 'subscriptions') fetchSubscriptions(subSkip);
    else if (tab === 'leads') fetchLeads(leadSkip);
    else if (tab === 'contacts') fetchContacts(contactSkip);
    else if (tab === 'transactions') fetchTransactions(txSkip);
    else if (tab === 'webinar') fetchWebinar();
  }, [tab, regSkip, subSkip, leadSkip, contactSkip, txSkip, fetchRegistrations, fetchSubscriptions, fetchLeads, fetchContacts, fetchTransactions, fetchWebinar]);

  const filterItems = (items) => {
    if (!searchTerm.trim()) return items;
    const term = searchTerm.toLowerCase();
    return items.filter(item =>
      Object.values(item).some(v => v && String(v).toLowerCase().includes(term))
    );
  };

  const exportCSV = (data, filename) => {
    if (!data.length) return;
    const keys = Object.keys(data[0]);
    const csv = [keys.join(','), ...data.map(row => keys.map(k => `"${String(row[k] ?? '').replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <div className="animate-pulse text-slate-400">Loading admin panel...</div>
      </div>
    );
  }

  if (!user || user.role !== 'admin') return null;

  const regColumns = [
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'status', label: 'Status', render: (v) => <StatusBadge status={v} /> },
    { key: 'created_at', label: 'Date', render: (v) => formatDate(v) },
  ];

  const subColumns = [
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'session_title', label: 'Session' },
    { key: 'provider', label: 'Provider', render: (v) => <span className="capitalize">{v}</span> },
    { key: 'status', label: 'Status', render: (v) => <StatusBadge status={v} /> },
    { key: 'created_at', label: 'Date', render: (v) => formatDate(v) },
  ];

  const leadColumns = [
    { key: 'email', label: 'Email' },
    { key: 'name', label: 'Name' },
    { key: 'source', label: 'Source' },
    { key: 'created_at', label: 'Date', render: (v) => formatDate(v) },
  ];

  const contactColumns = [
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'message', label: 'Message', render: (v) => <span className="max-w-[300px] truncate block">{v}</span> },
    { key: 'created_at', label: 'Date', render: (v) => formatDate(v) },
  ];

  const txColumns = [
    { key: 'product', label: 'Product', render: (v, row) => v || row.course_id || '—' },
    { key: 'amount', label: 'Amount', render: (v, row) => row.currency === 'INR' ? `₹${v}` : `$${v}` },
    { key: 'payment_method', label: 'Method', render: (v) => <span className="capitalize">{v}</span> },
    { key: 'payment_status', label: 'Status', render: (v) => <StatusBadge status={v} /> },
    { key: 'created_at', label: 'Date', render: (v) => formatDate(v) },
  ];

  return (
    <div data-testid="admin-page" className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8"
        >
          <div>
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck className="w-5 h-5 text-indigo-600" />
              <h1 className="font-serif text-3xl sm:text-4xl text-slate-900" data-testid="admin-title">Admin Dashboard</h1>
            </div>
            <p className="text-slate-500 text-sm">Manage registrations, subscriptions, and analytics</p>
          </div>
          <Button variant="outline" size="sm" className="rounded-full gap-2 self-start" onClick={() => { fetchStats(); setLoading(true); setTimeout(() => setLoading(false), 300); }} data-testid="refresh-stats-btn">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </Button>
        </motion.div>

        <Tabs value={tab} onValueChange={setTab} className="space-y-6">
          <TabsList className="bg-white border border-slate-200 rounded-full p-1 flex-wrap h-auto gap-0.5" data-testid="admin-tabs">
            <TabsTrigger value="overview" className="rounded-full text-xs sm:text-sm data-[state=active]:bg-slate-900 data-[state=active]:text-white px-3 sm:px-5" data-testid="tab-overview">
              <BarChart3 className="w-3.5 h-3.5 mr-1.5" /> Overview
            </TabsTrigger>
            <TabsTrigger value="registrations" className="rounded-full text-xs sm:text-sm data-[state=active]:bg-slate-900 data-[state=active]:text-white px-3 sm:px-5" data-testid="tab-registrations">
              <UserCheck className="w-3.5 h-3.5 mr-1.5" /> Registrations
            </TabsTrigger>
            <TabsTrigger value="subscriptions" className="rounded-full text-xs sm:text-sm data-[state=active]:bg-slate-900 data-[state=active]:text-white px-3 sm:px-5" data-testid="tab-subscriptions">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Subscriptions
            </TabsTrigger>
            <TabsTrigger value="leads" className="rounded-full text-xs sm:text-sm data-[state=active]:bg-slate-900 data-[state=active]:text-white px-3 sm:px-5" data-testid="tab-leads">
              <Mail className="w-3.5 h-3.5 mr-1.5" /> Leads
            </TabsTrigger>
            <TabsTrigger value="contacts" className="rounded-full text-xs sm:text-sm data-[state=active]:bg-slate-900 data-[state=active]:text-white px-3 sm:px-5" data-testid="tab-contacts">
              <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> Contacts
            </TabsTrigger>
            <TabsTrigger value="transactions" className="rounded-full text-xs sm:text-sm data-[state=active]:bg-slate-900 data-[state=active]:text-white px-3 sm:px-5" data-testid="tab-transactions">
              <CreditCard className="w-3.5 h-3.5 mr-1.5" /> Transactions
            </TabsTrigger>
            <TabsTrigger value="webinar" className="rounded-full text-xs sm:text-sm data-[state=active]:bg-slate-900 data-[state=active]:text-white px-3 sm:px-5" data-testid="tab-webinar">
              <Video className="w-3.5 h-3.5 mr-1.5" /> Webinar
            </TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              <StatCard icon={UserCheck} label="Masterclass Registrations" value={stats?.total_registrations ?? 0} color="bg-blue-50 text-blue-600" />
              <StatCard icon={RefreshCw} label="Active Subscriptions" value={stats?.active_subscriptions ?? 0} sub={`${stats?.total_subscriptions ?? 0} total`} color="bg-emerald-50 text-emerald-600" />
              <StatCard icon={IndianRupee} label="Revenue (INR)" value={`₹${(stats?.revenue_inr ?? 0).toLocaleString('en-IN')}`} sub={`+ ₹${(stats?.sub_revenue_inr ?? 0).toLocaleString('en-IN')}/mo subs`} color="bg-amber-50 text-amber-600" />
              <StatCard icon={DollarSign} label="Revenue (USD)" value={`$${(stats?.revenue_usd ?? 0).toLocaleString()}`} sub={`+ $${(stats?.sub_revenue_usd ?? 0).toLocaleString()}/mo subs`} color="bg-green-50 text-green-600" />
              <StatCard icon={Mail} label="Email Leads" value={stats?.total_leads ?? 0} color="bg-violet-50 text-violet-600" />
              <StatCard icon={MessageSquare} label="Contact Messages" value={stats?.total_contacts ?? 0} color="bg-rose-50 text-rose-600" />
              <StatCard icon={Users} label="Registered Users" value={stats?.total_users ?? 0} color="bg-indigo-50 text-indigo-600" />
              <StatCard icon={TrendingUp} label="Paid Transactions" value={stats?.paid_transactions ?? 0} sub={`${stats?.total_transactions ?? 0} total`} color="bg-teal-50 text-teal-600" />
            </motion.div>

            {/* Pricing Management */}
            <div className="bg-white rounded-2xl border border-slate-100 p-6 mt-6" data-testid="pricing-section">
              <h3 className="font-serif text-lg text-slate-900 mb-4 flex items-center gap-2">
                <IndianRupee className="w-4 h-4 text-amber-600" /> Masterclass Pricing
              </h3>
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Price (INR)</label>
                  <div className="flex items-center gap-1">
                    <span className="text-slate-400 text-sm">₹</span>
                    <Input type="number" value={pricingForm.price_inr} onChange={e => setPricingForm(p => ({...p, price_inr: e.target.value}))} className="w-28 h-9 text-sm" data-testid="pricing-inr-input" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Price (USD)</label>
                  <div className="flex items-center gap-1">
                    <span className="text-slate-400 text-sm">$</span>
                    <Input type="number" step="0.01" value={pricingForm.price_usd} onChange={e => setPricingForm(p => ({...p, price_usd: e.target.value}))} className="w-28 h-9 text-sm" data-testid="pricing-usd-input" />
                  </div>
                </div>
                <Button onClick={savePricing} disabled={pricingSaving} size="sm" className="rounded-full bg-slate-900 text-white gap-1.5 h-9" data-testid="save-pricing-btn">
                  <Save className="w-3.5 h-3.5" /> {pricingSaving ? 'Saving...' : 'Update Price'}
                </Button>
              </div>
              <p className="text-xs text-slate-400 mt-3">Changes apply immediately to checkout pages. Current: ₹{pricingData.price_inr} / ${pricingData.price_usd}</p>
            </div>
          </TabsContent>

          {/* Registrations */}
          <TabsContent value="registrations">
            <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-slate-100">
                <div className="flex items-center gap-3 flex-1">
                  <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input placeholder="Search registrations..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9 h-9 text-sm" data-testid="search-registrations" />
                  </div>
                </div>
                <Button variant="outline" size="sm" className="rounded-full gap-1.5 ml-2" onClick={() => exportCSV(registrations.items, 'registrations')} data-testid="export-registrations">
                  <Download className="w-3.5 h-3.5" /> Export
                </Button>
              </div>
              <DataTable columns={regColumns} data={filterItems(registrations.items)} emptyText="No masterclass registrations yet." testId="registrations-table" />
              <Pagination total={registrations.total} skip={regSkip} limit={LIMIT} onPageChange={setRegSkip} />
            </div>
          </TabsContent>

          {/* Subscriptions */}
          <TabsContent value="subscriptions">
            <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-slate-100">
                <div className="flex items-center gap-3 flex-1">
                  <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input placeholder="Search subscriptions..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9 h-9 text-sm" data-testid="search-subscriptions" />
                  </div>
                </div>
                <Button variant="outline" size="sm" className="rounded-full gap-1.5 ml-2" onClick={() => exportCSV(subscriptions.items, 'subscriptions')} data-testid="export-subscriptions">
                  <Download className="w-3.5 h-3.5" /> Export
                </Button>
              </div>
              <DataTable columns={subColumns} data={filterItems(subscriptions.items)} emptyText="No subscriptions yet." testId="subscriptions-table" />
              <Pagination total={subscriptions.total} skip={subSkip} limit={LIMIT} onPageChange={setSubSkip} />
            </div>
          </TabsContent>

          {/* Leads */}
          <TabsContent value="leads">
            <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-slate-100">
                <div className="flex items-center gap-3 flex-1">
                  <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input placeholder="Search leads..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9 h-9 text-sm" data-testid="search-leads" />
                  </div>
                </div>
                <Button variant="outline" size="sm" className="rounded-full gap-1.5 ml-2" onClick={() => exportCSV(leads.items, 'leads')} data-testid="export-leads">
                  <Download className="w-3.5 h-3.5" /> Export
                </Button>
              </div>
              <DataTable columns={leadColumns} data={filterItems(leads.items)} emptyText="No leads captured yet." testId="leads-table" />
              <Pagination total={leads.total} skip={leadSkip} limit={LIMIT} onPageChange={setLeadSkip} />
            </div>
          </TabsContent>

          {/* Contacts */}
          <TabsContent value="contacts">
            <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-slate-100">
                <div className="flex items-center gap-3 flex-1">
                  <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input placeholder="Search contacts..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9 h-9 text-sm" data-testid="search-contacts" />
                  </div>
                </div>
                <Button variant="outline" size="sm" className="rounded-full gap-1.5 ml-2" onClick={() => exportCSV(contacts.items, 'contacts')} data-testid="export-contacts">
                  <Download className="w-3.5 h-3.5" /> Export
                </Button>
              </div>
              <DataTable columns={contactColumns} data={filterItems(contacts.items)} emptyText="No contact messages yet." testId="contacts-table" />
              <Pagination total={contacts.total} skip={contactSkip} limit={LIMIT} onPageChange={setContactSkip} />
            </div>
          </TabsContent>

          {/* Transactions */}
          <TabsContent value="transactions">
            <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-slate-100">
                <div className="flex items-center gap-3 flex-1">
                  <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input placeholder="Search transactions..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9 h-9 text-sm" data-testid="search-transactions" />
                  </div>
                </div>
                <Button variant="outline" size="sm" className="rounded-full gap-1.5 ml-2" onClick={() => exportCSV(transactions.items, 'transactions')} data-testid="export-transactions">
                  <Download className="w-3.5 h-3.5" /> Export
                </Button>
              </div>
              <DataTable columns={txColumns} data={filterItems(transactions.items)} emptyText="No transactions yet." testId="transactions-table" />
              <Pagination total={transactions.total} skip={txSkip} limit={LIMIT} onPageChange={setTxSkip} />
            </div>
          </TabsContent>

          {/* Webinar Management */}
          <TabsContent value="webinar">
            <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden p-6" data-testid="webinar-settings">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-serif text-xl text-slate-900">Webinar Settings</h3>
                <div className="flex gap-2">
                  <a href="/webinar" target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm" className="rounded-full gap-1.5" data-testid="preview-webinar-btn">
                      <ExternalLink className="w-3.5 h-3.5" /> Preview
                    </Button>
                  </a>
                </div>
              </div>
              {webinarForm && (
                <div className="space-y-5 max-w-2xl">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">YouTube Video ID or URL</label>
                    <Input value={webinarForm.youtube_id || ''} onChange={e => setWebinarForm(p => ({...p, youtube_id: e.target.value}))} placeholder="e.g. QVOjr7bf5Bg or https://youtu.be/QVOjr7bf5Bg" className="text-sm" data-testid="webinar-youtube-id" />
                    <p className="text-xs text-slate-400 mt-1">Paste the full YouTube URL or just the video ID. The system auto-extracts the ID. <strong>Note:</strong> The video must have embedding enabled (not restricted by uploader).</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Webinar Title</label>
                    <Input value={webinarForm.title || ''} onChange={e => setWebinarForm(p => ({...p, title: e.target.value}))} placeholder="Heal & Manifest Live Webinar" className="text-sm" data-testid="webinar-title-input" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Description</label>
                    <textarea value={webinarForm.description || ''} onChange={e => setWebinarForm(p => ({...p, description: e.target.value}))} rows={3} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10" placeholder="Webinar description..." data-testid="webinar-description-input" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Scheduled Date & Time (for "Fake Live")</label>
                    <Input type="datetime-local" value={webinarForm.scheduled_at ? webinarForm.scheduled_at.slice(0, 16) : ''} onChange={e => setWebinarForm(p => ({...p, scheduled_at: e.target.value ? new Date(e.target.value).toISOString() : ''}))} className="text-sm" data-testid="webinar-scheduled-at" />
                    <p className="text-xs text-slate-400 mt-1">Leave empty for on-demand only mode. Set a future date for "fake live" countdown experience.</p>
                  </div>
                  <div className="flex flex-wrap gap-6">
                    <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                      <input type="checkbox" checked={webinarForm.allow_on_demand ?? true} onChange={e => setWebinarForm(p => ({...p, allow_on_demand: e.target.checked}))} className="rounded" data-testid="webinar-on-demand-toggle" />
                      Allow On-Demand Viewing
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                      <input type="checkbox" checked={webinarForm.chat_enabled ?? true} onChange={e => setWebinarForm(p => ({...p, chat_enabled: e.target.checked}))} className="rounded" data-testid="webinar-chat-toggle" />
                      Enable Live Chat
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                      <input type="checkbox" checked={webinarForm.is_active ?? true} onChange={e => setWebinarForm(p => ({...p, is_active: e.target.checked}))} className="rounded" data-testid="webinar-active-toggle" />
                      Webinar Active
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                      <input type="checkbox" checked={webinarForm.require_payment ?? true} onChange={e => setWebinarForm(p => ({...p, require_payment: e.target.checked}))} className="rounded" data-testid="webinar-payment-toggle" />
                      Require Payment (Masterclass)
                    </label>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button onClick={saveWebinar} disabled={webinarSaving} className="rounded-full bg-slate-900 text-white gap-2" data-testid="save-webinar-btn">
                      <Save className="w-4 h-4" /> {webinarSaving ? 'Saving...' : 'Save Settings'}
                    </Button>
                    <Button onClick={clearChat} variant="outline" className="rounded-full gap-2 text-red-600 hover:bg-red-50" data-testid="clear-chat-btn">
                      <Trash2 className="w-4 h-4" /> Clear Chat History
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
